import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Button from "../UI/Button";
import { ButtonTypes } from "../../utils/Constants";
import usePageTitle from "../../utils/usePageTitle";

const Login: React.FC = () => {
  usePageTitle("Log in");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<{ username?: string; password?: string; general?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    const newErr: typeof errors = {};
    if (!username.trim()) newErr.username = "Please enter your email or username.";
    if (!password) newErr.password = "Please enter your password.";
    setErrors(newErr);
    if (Object.keys(newErr).length) return;

    try {
      setSubmitting(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/login/`, { login: username.trim(), password });
      const result = response.data;
      if (response.status === 200) {

        localStorage.setItem(
          "auth_data",
          JSON.stringify({
            access: result.access,
            refresh: result.refresh,
            id: result.id,
            firstName: result.first_name,
            lastName: result.last_name,
            username: result.username,
            email: result.email,
          })
        );

        localStorage.setItem("access_token", result.access);
        axios.defaults.headers.common['Authorization'] = `Bearer ${result.access}`;
        window.dispatchEvent(new Event("auth-changed"));
        navigate("/", { replace: true });
      } else {
        console.error("Error logging in:", response.status, result);
      }
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        if (status === 401 || status === 400) {
          const msg =
            (data?.detail as string) ||
            (typeof data === "string" ? data : "") ||
            "Login failed. Check your username/email and password.";
          setErrors({ general: msg });
        } else {
          setErrors({ general: "Something went wrong. Please try again." });
        }
      } else {
        setErrors({ general: "Network error. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen h-full bg-secondary">
      <div
        className="w-[90%] max-w-md my-4 p-8 bg-white rounded-lg shadow-lg"
      >
        <h2 className="mb-4 text-sm font-medium text-gray-500">WELCOME BACK</h2>
        <h3 className="mb-6 text-2xl font-semibold text-gray-800">
          Log in to your account
        </h3>

        {errors.general && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
            {errors.general}
          </div>
        )}

        <div className="mb-4">
          <label
            className="block mb-2 text-sm font-medium text-gray-700"
            htmlFor="username"
          >
            Email or username
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (errors.username)
                setErrors(prev => ({ ...prev, username: undefined }));
            }}
            className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.username ? "border-red-400" : ""}`}
            placeholder="Email or username"
          />
          {errors.username && <p className="mt-1 text-xs text-red-600">{errors.username}</p>}
        </div>
        <div className="mb-6">
          <label
            className="block mb-2 text-sm font-medium text-gray-700"
            htmlFor="password"
          >
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password)
                setErrors(prev => ({ ...prev, password: undefined }));
            }}
            className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.password ? "border-red-400" : ""}`}
            placeholder="Password"
          />
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
        </div>
        <Button type={ButtonTypes.PRIMARY} onClick={handleSubmit} className="w-full !px-4 !py-2 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a423a]" disabled={submitting}>
          {submitting ? "Logging in..." : "Login"}
        </Button>
        <p className="mt-4 text-sm text-center text-gray-600">
          New User?{" "}
          <a
            href="/signup"
            className="font-semibold text-gray-800 hover:underline"
          >
            SIGN UP HERE
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
